import { Vector3 } from 'three'
import { TREE_CONFIG } from './constants'

/**
 * 简单的噪声函数 - 用于生成不规则边界
 */
function simpleNoise(x: number, y: number, z: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233 + z * 37.719) * 43758.5453
  return n - Math.floor(n)
}

/**
 * 生成不规则空间内的随机坐标（混沌状态）
 * 使用噪声函数创建不规则的边界形状
 */
export function generateChaosPosition(radius: number = TREE_CONFIG.chaosRadius): Vector3 {
  const theta = Math.random() * Math.PI * 2
  const phi = Math.acos(2 * Math.random() - 1)
  
  // 基础半径
  const baseR = Math.cbrt(Math.random()) * radius
  
  // 使用噪声函数创建不规则边界
  // 根据角度生成不同的半径变化
  const noiseScale = 0.4 // 不规则程度
  const noise1 = simpleNoise(theta * 2, phi * 2, 0) * noiseScale
  const noise2 = simpleNoise(theta * 3, phi * 1.5, 1) * noiseScale * 0.5
  const noise3 = simpleNoise(theta * 5, phi * 4, 2) * noiseScale * 0.25
  
  // 组合噪声创建不规则半径
  const irregularFactor = 1 + noise1 + noise2 + noise3 - noiseScale * 0.875
  const r = baseR * irregularFactor
  
  // 添加一些随机偏移使边界更加不规则
  const offsetX = (Math.random() - 0.5) * radius * 0.2
  const offsetY = (Math.random() - 0.5) * radius * 0.2
  const offsetZ = (Math.random() - 0.5) * radius * 0.2
  
  return new Vector3(
    r * Math.sin(phi) * Math.cos(theta) + offsetX,
    r * Math.sin(phi) * Math.sin(theta) + TREE_CONFIG.height / 2 + offsetY,
    r * Math.cos(phi) + offsetZ
  )
}

/**
 * 生成圆锥形树形状的坐标（成形状态）
 */
export function generateTreePosition(
  heightRatio: number, // 0-1, 0是底部, 1是顶部
  angleOffset: number = 0
): Vector3 {
  const height = heightRatio * TREE_CONFIG.height
  // 圆锥形：半径随高度线性减小
  const radius = TREE_CONFIG.baseRadius * (1 - heightRatio * 0.9)
  const angle = Math.random() * Math.PI * 2 + angleOffset
  
  // 添加一些随机偏移使树看起来更自然
  const radiusVariation = radius * (0.8 + Math.random() * 0.4)
  
  return new Vector3(
    radiusVariation * Math.cos(angle),
    height,
    radiusVariation * Math.sin(angle)
  )
}

/**
 * 生成针叶粒子的目标位置（圆锥形分布）
 */
export function generateFoliagePosition(): Vector3 {
  const heightRatio = Math.random()
  return generateTreePosition(heightRatio)
}

/**
 * 生成装饰物的目标位置（在树表面）
 */
export function generateOrnamentPosition(
  type: 'gift' | 'bauble' | 'light'
): Vector3 {
  let heightRatio: number
  
  switch (type) {
    case 'gift':
      // 礼物主要在底部
      heightRatio = Math.random() * 0.3
      break
    case 'bauble':
      // 彩球分布偏向下层，70%在下半部分，30%在上半部分
      const rawHeight = Math.random()
      if (rawHeight < 0.7) {
        heightRatio = 0.1 + rawHeight * 0.5  // 下层: 0.1-0.45
      } else {
        heightRatio = 0.45 + (rawHeight - 0.7) * 1.5  // 上层: 0.45-0.9
      }
      break
    case 'light':
      // 灯光均匀分布
      heightRatio = Math.random() * 0.95
      break
    default:
      heightRatio = Math.random()
  }
  
  const position = generateTreePosition(heightRatio)
  
  // 装饰物稍微向外偏移，使其在树表面
  const outwardOffset = type === 'light' ? 0.1 : 0.2
  position.x *= (1 + outwardOffset)
  position.z *= (1 + outwardOffset)
  
  return position
}

/**
 * 生成拍立得照片的位置
 */
export function generatePolaroidPositions(count: number): Vector3[] {
  const positions: Vector3[] = []
  const angleStep = (Math.PI * 2) / count
  
  for (let i = 0; i < count; i++) {
    const angle = i * angleStep + Math.random() * 0.3
    const heightRatio = 0.3 + Math.random() * 0.4 // 中部区域
    const radius = TREE_CONFIG.baseRadius * (1 - heightRatio * 0.7) + 0.5
    
    positions.push(new Vector3(
      radius * Math.cos(angle),
      heightRatio * TREE_CONFIG.height,
      radius * Math.sin(angle)
    ))
  }
  
  return positions
}

/**
 * 线性插值
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t
}

/**
 * Vector3 线性插值
 */
export function lerpVector3(start: Vector3, end: Vector3, t: number): Vector3 {
  return new Vector3(
    lerp(start.x, end.x, t),
    lerp(start.y, end.y, t),
    lerp(start.z, end.z, t)
  )
}

/**
 * 缓动函数
 */
export function easeInOut(t: number): number {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - Math.pow(-2 * t + 2, 3) / 2
}

export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

export function easeIn(t: number): number {
  return t * t * t
}

/**
 * 根据权重计算延迟的进度
 * 权重越大（越重），延迟越多
 */
export function getWeightedProgress(progress: number, weight: number): number {
  const delay = weight * 0.3 // 最大延迟 30%
  const adjustedProgress = Math.max(0, (progress - delay) / (1 - delay))
  return easeInOut(adjustedProgress)
}

/**
 * 映射范围
 */
export function mapRange(
  value: number,
  inMin: number,
  inMax: number,
  outMin: number,
  outMax: number
): number {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin
}

/**
 * 限制值在范围内
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}